import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../hooks/useTheme';
import API_CONFIG from '../config/api';
import { truncateEmail } from '../utils/stringUtils';

const Sidebar = memo(({ isMobileMenuOpen, closeMobileMenu }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const getProfilePictureUrl = useMemo(() => {
    if (user?.profile_picture) {
      return `${API_CONFIG.UPLOADS_BASE_URL}/profile_pictures/${user.profile_picture}`;
    }
    return null;
  }, [user?.profile_picture]);

  const displayEmail = useMemo(() => truncateEmail(user?.email), [user?.email]);

  const handleLogout = () => {
    logout();
    if (closeMobileMenu) closeMobileMenu();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinkClass = (path) =>
    `block px-4 py-2 rounded-lg transition-colors ${
      isActive(path)
        ? 'bg-primary-50 dark:bg-surface-dark-card border border-primary-200 dark:border-surface-dark-border text-primary-800 dark:text-white font-semibold'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-dark-raised'
    }`;

  return (
    <aside className={`
      fixed left-0 top-0 h-dvh w-64 bg-surface-card dark:bg-surface-dark shadow-lg z-50 transition-all duration-300
      ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
    `}>
      <nav className="flex flex-col h-full">
        <div className="flex items-center justify-center p-6">
          <Link
            to={user ? '/home' : '/'}
            className="text-xl font-bold text-gray-900 dark:text-white hover:text-primary-600 transition-colors"
            onClick={closeMobileMenu}
          >
            Vegetablah
          </Link>
        </div>

        <ul className="flex-1 overflow-y-auto p-6 space-y-2">
          {user ? (
            // Authenticated user navigation
            <>
              <li>
                <Link
                  to="/home"
                  className={navLinkClass('/home')}
                  onClick={closeMobileMenu}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/you"
                  className={navLinkClass('/you')}
                  onClick={closeMobileMenu}
                >
                  You
                </Link>
              </li>
              <li>
                <Link
                  to="/log"
                  className={navLinkClass('/log')}
                  onClick={closeMobileMenu}
                >
                  Log
                </Link>
              </li>
              <li>
                <Link
                  to="/data"
                  className={navLinkClass('/data')}
                  onClick={closeMobileMenu}
                >
                  Data
                </Link>
              </li>
              <li>
                <Link
                  to="/insights"
                  className={navLinkClass('/insights')}
                  onClick={closeMobileMenu}
                >
                  Insights
                </Link>
              </li>
              <li>
                <Link
                  to="/experiments"
                  className={navLinkClass('/experiments')}
                  onClick={closeMobileMenu}
                >
                  Experiments
                </Link>
              </li>
              <li>
                <Link
                  to="/supplements"
                  className={navLinkClass('/supplements')}
                  onClick={closeMobileMenu}
                >
                  Supplements
                </Link>
              </li>
            </>
          ) : (
            // Unauthenticated user navigation
            <>
              <li>
                <Link
                  to="/articles"
                  className={navLinkClass('/articles')}
                  onClick={closeMobileMenu}
                >
                  Articles
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={navLinkClass('/about')}
                  onClick={closeMobileMenu}
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={navLinkClass('/contact')}
                  onClick={closeMobileMenu}
                >
                  Contact
                </Link>
              </li>
            </>
          )}
        </ul>

        <div className="p-6">
          {user ? (
            // Authenticated user dropdown
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-solar-gradient flex items-center justify-center text-[#1a1200] font-semibold mr-3 overflow-hidden">
                  {user?.profile_picture ? (
                    <img
                      src={getProfilePictureUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    user?.email?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {displayEmail}
                  </div>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2">
                  <Link
                    to="/me"
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      if (closeMobileMenu) closeMobileMenu();
                    }}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={toggleTheme}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {theme === 'dark' ? (
                      <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                      </svg>
                    )}
                    {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsDropdownOpen(false);
                      if (closeMobileMenu) closeMobileMenu();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Unauthenticated user footer
            <div className="flex flex-col items-start">
              <Link
                to="/login"
                className="inline-block w-24 bg-gray-700 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium text-center mb-2"
                onClick={closeMobileMenu}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="inline-block w-24 bg-gray-700 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors font-medium text-center mb-3"
                onClick={closeMobileMenu}
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
